import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SeenIcon } from '@/components/common/SeenLogo';
import { 
  Scan, 
  Upload, 
  History, 
  User, 
  LogOut, 
  Menu,
  X,
  LayoutDashboard,
  Building2,
  Crown,
  Ticket,
  Shield,
  Store,
  Search
} from 'lucide-react';

export const Navigation = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { href: '/dashboard', label: t('common.dashboard'), icon: LayoutDashboard },
    { href: '/scan', label: t('common.scan'), icon: Scan },
    { href: '/search', label: 'Search', icon: Search },
    { href: '/upload', label: 'Upload', icon: Upload },
    { href: '/history', label: t('common.history'), icon: History },
    { href: '/festival', label: t('common.festival'), icon: Ticket },
    { href: '/stores', label: 'Stores', icon: Store },
    { href: '/company', label: t('common.company'), icon: Building2 },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="sticky top-0 z-50 glass border-b border-border/50" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to={isAuthenticated ? '/dashboard' : '/'} 
            className="flex items-center group flex-shrink-0"
            data-testid="nav-logo"
          >
            <SeenIcon size={32} />
          </Link>

          {/* Desktop Navigation - Only show on xl screens */}
          {isAuthenticated && (
            <div className="hidden xl:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive(link.href)
                      ? 'text-foreground bg-accent'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  <span className="flex items-center gap-2">
                    <link.icon className="w-4 h-4" />
                    {link.label}
                  </span>
                </Link>
              ))}
            </div>
          )}

          {/* Right side */}
          <div className="flex items-center gap-2">
            {isAuthenticated ? (
              <>
                {/* User Menu */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      className="flex items-center gap-2 px-2 sm:px-3"
                      data-testid="user-menu-trigger"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                      <span className="hidden lg:block text-sm font-medium max-w-[80px] truncate">
                        {user?.name?.split(' ')[0]}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <div className="px-2 py-1.5">
                      <p className="text-sm font-medium">{user?.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center gap-2" data-testid="nav-profile">
                        <User className="w-4 h-4" />
                        {t('common.profile')}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/subscription" className="flex items-center gap-2" data-testid="nav-subscription">
                        <Crown className="w-4 h-4" />
                        {t('common.subscription')}
                      </Link>
                    </DropdownMenuItem>
                    {(user?.subscription_plan === 'festival' || user?.subscription_plan === 'enterprise' || user?.role === 'admin') && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin">
                          <Shield className="w-4 h-4" />
                          Admin Dashboard
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={handleLogout}
                      className="text-destructive focus:text-destructive"
                      data-testid="nav-logout"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('common.logout')}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                {/* Mobile/Tablet Menu Button - Show on screens smaller than xl */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="xl:hidden"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-toggle"
                >
                  {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link to="/browse-stores">
                  <Button variant="ghost" size="sm" className="hidden sm:inline-flex gap-1.5" data-testid="nav-browse-stores">
                    <Store className="w-4 h-4" />
                    Find Stores
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="ghost" size="sm" className="hidden sm:inline-flex" data-testid="nav-login">Log in</Button>
                </Link>
                <Link to="/auth">
                  <Button size="sm" data-testid="nav-signup">Get Started</Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Mobile/Tablet Navigation - Show on screens smaller than xl */}
        {isAuthenticated && mobileMenuOpen && (
          <div className="xl:hidden py-4 border-t border-border/50 animate-slide-up" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? 'text-foreground bg-accent'
                      : 'text-muted-foreground hover:text-foreground hover:bg-accent/50'
                  }`}
                >
                  <link.icon className="w-5 h-5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
